import {
  Box,
  Button,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { AdminPanelSettings } from "@mui/icons-material";
import { AdminUser } from "types/interfaces/adminUsers";
import { ReactNode } from "react";
import { GetAdminUsersParams } from "features/adminUsers/adminUsers";
import CustomScrollbars from "../CustomScrollbars";

interface UserManagementTableProps {
  users: AdminUser[];
  isLoading: boolean;
  isFetching: boolean;
  selectedUserId?: number;
  onManage: (user: AdminUser) => void;
  pagination?: ReactNode;
  sortBy: GetAdminUsersParams["sort_by"];
  sortOrder: GetAdminUsersParams["sort_order"];
  onSortChange: (field: GetAdminUsersParams["sort_by"]) => void;
}

function RoleChips({ user }: { user: AdminUser }) {
  const { profile } = user;
  const hasAny =
    user.is_superuser ||
    profile.is_web_user ||
    profile.is_integration_user ||
    profile.is_migration_user ||
    profile.is_calibrate_admin ||
    profile.is_chat_bot_allowed;
  return (
    <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
      {user.is_superuser && (
        <Chip
          icon={<AdminPanelSettings sx={{ fontSize: 16, color: "inherit !important" }} />}
          label="Super Admin"
          size="small"
          sx={{ bgcolor: "primary.main", color: "primary.contrastText", fontWeight: 600 }}
        />
      )}
      {profile.is_migration_user && (
        <Chip
          label="Migration"
          size="small"
          sx={{ bgcolor: "#fff8e1", color: "#ff8f00", fontWeight: 600 }}
        />
      )}
      {profile.is_integration_user && (
        <Chip label="Integration" size="small" sx={{ bgcolor: "#fff3e0", color: "#e65100", fontWeight: 600 }} />
      )}
      {profile.is_web_user && (
        <Chip label="Web User" size="small" sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 600 }} />
      )}
      {profile.is_chat_bot_allowed && (
        <Chip label="Chat Bot" size="small" sx={{ bgcolor: "#e0f2f1", color: "#00695c", fontWeight: 600 }} />
      )}
      {profile.is_calibrate_admin && (
        <Chip label="Admin" size="small" sx={{ bgcolor: "#f3e5f5", color: "#6a1b9a", fontWeight: 600 }} />
      )}
      {!hasAny && (
        <Typography variant="caption" color="text.disabled">—</Typography>
      )}
    </Box>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <TableRow key={i}>
          {Array.from({ length: 8 }).map((_, j) => (
            <TableCell key={j}>
              <Skeleton variant="text" width={j === 2 ? 120 : "80%"} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

export default function UserManagementTable({
  users,
  isLoading,
  isFetching,
  selectedUserId,
  onManage,
  pagination,
  sortBy,
  sortOrder,
  onSortChange,
}: UserManagementTableProps) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <CustomScrollbars sx={{ flex: 1, minHeight: 0 }} trackOffsetTop={40}>
        <Table stickyHeader size="small" sx={{ minWidth: 680 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "name"}
                  direction={sortBy === "name" ? sortOrder ?? "asc" : "asc"}
                  onClick={() => onSortChange("name")}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "email"}
                  direction={sortBy === "email" ? sortOrder ?? "asc" : "asc"}
                  onClick={() => onSortChange("email")}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Roles</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "folders"}
                  direction={sortBy === "folders" ? sortOrder ?? "asc" : "asc"}
                  onClick={() => onSortChange("folders")}
                >
                  Folders
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "integrations"}
                  direction={sortBy === "integrations" ? sortOrder ?? "asc" : "asc"}
                  onClick={() => onSortChange("integrations")}
                >
                  Integrations
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "date_joined"}
                  direction={sortBy === "date_joined" ? sortOrder ?? "asc" : "asc"}
                  onClick={() => onSortChange("date_joined")}
                >
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "last_login"}
                  direction={sortBy === "last_login" ? sortOrder ?? "asc" : "asc"}
                  onClick={() => onSortChange("last_login")}
                >
                  Last Login
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <SkeletonRows />
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">No users found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow
                  key={user.id}
                  hover
                  selected={user.id === selectedUserId}
                  sx={{
                    opacity: isFetching ? 0.6 : 1,
                    cursor: "pointer",
                    "&.Mui-selected": { bgcolor: "rgba(31,93,107,0.06)" },
                    "&.Mui-selected:hover": { bgcolor: "rgba(31,93,107,0.10)" },
                  }}
                  onClick={() => onManage(user)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {user.first_name || user.last_name
                        ? `${user.first_name} ${user.last_name}`.trim()
                        : user.username}
                    </Typography>
                    {(user.first_name || user.last_name) && (
                      <Typography variant="caption" color="text.secondary">
                        @{user.username}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.email || "—"}</Typography>
                  </TableCell>
                  <TableCell>
                    <RoleChips user={user} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.folder_count}
                      {user.shared_folder_count > 0 && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          + {user.shared_folder_count} shared
                        </Typography>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.integration_count}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(user.date_joined).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.last_login
                        ? new Date(user.last_login).toLocaleString(undefined, {
                            dateStyle: "short",
                            timeStyle: "short",
                          })
                        : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant={user.id === selectedUserId ? "contained" : "outlined"}
                      onClick={(e) => { e.stopPropagation(); onManage(user); }}
                      sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                      Manage
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CustomScrollbars>
      {pagination}
    </Box>
  );
}
